/**
 * Xero Connection Status Component
 * 
 * Displays the current Xero connection status and provides
 * a way to reconnect/reauthorize when tokens expire.
 */

import React, { useState } from 'react';
import {
  Alert,
  Button,
  Space,
  Tag,
  Modal,
  Spin,
  Typography,
  Card,
  Statistic,
  Row,
  Col,
  message,
} from 'antd';
import {
  CheckCircleOutlined,
  ExclamationCircleOutlined,
  CloseCircleOutlined,
  SyncOutlined,
  LinkOutlined,
  CloudOutlined,
} from '@ant-design/icons';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { xeroApi } from '../../services/api';

const { Text, Paragraph } = Typography;

interface XeroHealthResponse {
  status: 'healthy' | 'degraded' | 'failed' | 'not_configured';
  authenticated: boolean;
  responseTime: number;
  error?: string;
}

interface XeroConnectionStatusProps {
  /** Show as a compact inline badge instead of full card */
  compact?: boolean;
  /** Called when connection status changes */
  onStatusChange?: (isConnected: boolean) => void;
}

const XeroConnectionStatus: React.FC<XeroConnectionStatusProps> = ({
  compact = false,
  onStatusChange,
}) => {
  const queryClient = useQueryClient();
  const [connectModalVisible, setConnectModalVisible] = useState(false);

  // Check Xero health
  const {
    data: health,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ['xero-health'],
    queryFn: async () => {
      const response = await xeroApi.getHealth();
      // Note: axios interceptor already unwraps response.data
      const data = response as unknown as XeroHealthResponse;
      onStatusChange?.(data.authenticated);
      return data;
    },
    staleTime: 60000, // Check every minute
    refetchInterval: 60000,
    retry: 1,
  });

  // Get auth URL mutation
  const getAuthUrlMutation = useMutation({
    mutationFn: async () => {
      // Include current URL as return URL so user comes back after auth
      const returnUrl = window.location.href;
      const response = await xeroApi.getAuthUrl(returnUrl);
      // Note: axios interceptor already unwraps response.data
      return response as unknown as { authUrl: string };
    },
    onSuccess: (data) => {
      // Open Xero auth in a new window/tab
      const authWindow = window.open(data.authUrl, '_blank', 'width=600,height=700');
      
      // Poll for when auth is complete
      const pollInterval = setInterval(async () => {
        try {
          const healthResponse = await xeroApi.getHealth();
          // Note: axios interceptor already unwraps response.data
          const healthData = healthResponse as unknown as XeroHealthResponse;
          
          if (healthData.authenticated) {
            clearInterval(pollInterval);
            authWindow?.close();
            queryClient.invalidateQueries({ queryKey: ['xero-health'] });
            setConnectModalVisible(false);
            message.success('Successfully connected to Xero!');
          }
        } catch (e) {
          // Ignore errors during polling
        }
      }, 2000);

      // Stop polling after 5 minutes
      setTimeout(() => {
        clearInterval(pollInterval);
      }, 300000);
    },
    onError: (error: Error) => {
      message.error(`Failed to get authorization URL: ${error.message}`);
    },
  });

  // Determine status display
  const getStatusDisplay = () => {
    if (isLoading) {
      return {
        icon: <SyncOutlined spin />,
        color: 'default',
        text: 'Checking...',
        type: 'info' as const,
      };
    }

    if (error || !health) {
      return {
        icon: <CloseCircleOutlined />,
        color: 'error',
        text: 'Connection Error',
        type: 'error' as const,
      };
    }

    if (health.status === 'not_configured') {
      return {
        icon: <ExclamationCircleOutlined />,
        color: 'warning',
        text: 'Not Configured',
        type: 'warning' as const,
      };
    }

    if (health.authenticated && health.status === 'healthy') {
      return {
        icon: <CheckCircleOutlined />,
        color: 'success',
        text: 'Connected',
        type: 'success' as const,
      };
    }

    return {
      icon: <ExclamationCircleOutlined />,
      color: 'warning',
      text: 'Not Authenticated',
      type: 'warning' as const,
    };
  };

  const statusDisplay = getStatusDisplay();
  const needsAuth = !health?.authenticated && health?.status !== 'not_configured';

  // Compact mode - just show a badge
  if (compact) {
    return (
      <Space>
        <Tag
          icon={statusDisplay.icon}
          color={statusDisplay.color}
          style={{ cursor: needsAuth ? 'pointer' : 'default' }}
          onClick={needsAuth ? () => setConnectModalVisible(true) : undefined}
        >
          Xero: {statusDisplay.text}
        </Tag>
        {needsAuth && (
          <Button
            type="link"
            size="small"
            icon={<LinkOutlined />}
            onClick={() => setConnectModalVisible(true)}
          >
            Connect
          </Button>
        )}
      </Space>
    );
  }

  // Full mode - show alert with details
  return (
    <>
      {needsAuth ? (
        <Alert
          message="Xero Connection Required"
          description={
            <Space direction="vertical" style={{ width: '100%' }}>
              <Text>
                {health?.error || 'Your Xero connection has expired or is not set up. Please reconnect to enable payment sync.'}
              </Text>
              <Button
                type="primary"
                icon={<LinkOutlined />}
                onClick={() => setConnectModalVisible(true)}
                loading={getAuthUrlMutation.isPending}
              >
                Connect to Xero
              </Button>
            </Space>
          }
          type="warning"
          showIcon
          icon={<ExclamationCircleOutlined />}
          style={{ marginBottom: 16 }}
        />
      ) : health?.authenticated ? (
        <Alert
          message="Xero Connected"
          description={
            <Space>
              <Text>Your Xero account is connected and ready for payment sync.</Text>
              <Text type="secondary">Response time: {health.responseTime}ms</Text>
            </Space>
          }
          type="success"
          showIcon
          icon={<CheckCircleOutlined />}
          style={{ marginBottom: 16 }}
          action={
            <Button size="small" onClick={() => refetch()}>
              Refresh
            </Button>
          }
        />
      ) : health?.status === 'not_configured' ? (
        <Alert
          message="Xero Not Configured"
          description="Xero integration is not configured on this server. Contact your administrator."
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
        />
      ) : null}

      {/* Connect Modal */}
      <Modal
        title={
          <Space>
            <CloudOutlined />
            <span>Connect to Xero</span>
          </Space>
        }
        open={connectModalVisible}
        onCancel={() => setConnectModalVisible(false)}
        footer={[
          <Button key="cancel" onClick={() => setConnectModalVisible(false)}>
            Cancel
          </Button>,
          <Button
            key="connect"
            type="primary"
            icon={<LinkOutlined />}
            onClick={() => getAuthUrlMutation.mutate()}
            loading={getAuthUrlMutation.isPending}
          >
            Open Xero Login
          </Button>,
        ]}
        width={500}
      >
        <Space direction="vertical" style={{ width: '100%' }}>
          <Paragraph>
            Click the button below to open Xero's login page in a new window. After you
            authorize access, this page will automatically update.
          </Paragraph>

          <Card size="small" style={{ backgroundColor: '#f6f8fa' }}>
            <Space direction="vertical">
              <Text strong>What happens:</Text>
              <ol style={{ margin: 0, paddingLeft: 20 }}>
                <li>A new window will open with Xero's login page</li>
                <li>Log in to your Xero account</li>
                <li>Authorize Atlas to access your Xero data</li>
                <li>The window will close automatically</li>
              </ol>
            </Space>
          </Card>

          {health?.error && (
            <Alert
              message="Previous Error"
              description={health.error}
              type="error"
              showIcon
              style={{ marginTop: 16 }}
            />
          )}
        </Space>
      </Modal>
    </>
  );
};

export default XeroConnectionStatus;
